import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import styles from './LeadershipGuide.module.css';
import { Shield, Users, TrendingUp, Brain, Package, Wrench, MonitorSmartphone, Heart, Crown, ChevronDown, ChevronUp } from 'lucide-react';

interface RoleGuide {
  id: string;
  title: string;
  icon: React.ReactElement;
  department: string;
  overview: string;
  fleetRole: string;
  responsibilities: string[];
  commands: string[];
  principles: string[];
  notes?: string;
}

const roleGuides: RoleGuide[] = [
  {
    id: 'ceo',
    title: 'Chief Executive Officer (CEO)',
    icon: <Crown className={styles.roleIcon} />,
    department: 'Executive',
    overview: 'The CEO serves as Fleet Commander (FLEETCOM) during massive or combat-enabled fleet operations. Holds ultimate rank and authority, with the ability to delegate command to the COO.',
    fleetRole: 'Fleet Commander (FLEETCOM)',
    responsibilities: [
      'Establish the strategic objective and mission goals',
      'Direct high-level fleet maneuvers',
      'Issue fleet-wide commands',
      'Coordinate all battle groups',
      'Maintain operation-wide cohesion',
      'Delegate FLEETCOM authority to COO when appropriate'
    ],
    commands: ['All Battle Groups', 'Fleet-wide assets', 'Strategic reserves'],
    principles: [
      'Fleet Commander sets strategy; XO ensures execution',
      'Provide clear, decisive leadership during critical moments',
      'Maintain big-picture awareness across all divisions',
      'Trust Battle Group Commanders with their domains',
      'Delegate command to COO as needed for operational flexibility'
    ],
    notes: 'Not responsible for: Micro-tactics, escort decisions, hauling routes, or individual squadron control. CEO and COO are interchangeable in fleet command capability, with CEO holding rank.'
  },
  {
    id: 'coo',
    title: 'Chief Operating Officer (COO)',
    icon: <Users className={styles.roleIcon} />,
    department: 'Operations',
    overview: 'The COO serves as Fleet Executive Officer (Fleet XO) and second-in-command. Capable of assuming FLEETCOM role when delegated by CEO or when CEO is unavailable. Interchangeable with CEO in fleet command capability.',
    fleetRole: 'Fleet Executive Officer (Fleet XO) / FLEETCOM (when delegated)',
    responsibilities: [
      'Translate strategic orders into actionable directives',
      'Maintain operational tempo',
      'Synchronize Battle Group Commanders',
      'Coordinate timing across divisions',
      'Assume FLEETCOM command when delegated by CEO',
      'Take full command if CEO is unavailable'
    ],
    commands: ['All Battle Groups', 'Cross-division coordination', 'Operational execution', 'Full fleet command (when delegated)'],
    principles: [
      'Execute strategy operationally',
      'Synchronize all divisions',
      'Uphold discipline and tempo',
      'Bridge command and execution',
      'Ready to assume FLEETCOM at any time'
    ],
    notes: 'CEO and COO hold the same fleet command responsibilities and capabilities. COO serves as XO but can be delegated FLEETCOM authority. CEO holds ultimate rank.'
  },
  {
    id: 'chro',
    title: 'Chief Human Resource Officer (CHRO)',
    icon: <Users className={styles.roleIcon} />,
    department: 'Operations',
    overview: 'The CHRO manages recruitment, member onboarding, and personnel issues. Ensures the organization has the right people in the right positions.',
    fleetRole: 'Personnel & Training Coordinator',
    responsibilities: [
      'Manage recruitment and member onboarding',
      'Oversee personnel assignments and promotions',
      'Coordinate training programs across divisions',
      'Handle personnel issues and conflict resolution',
      'Maintain organizational roster and records'
    ],
    commands: ['Training operations', 'Personnel assignments', 'Member onboarding programs'],
    principles: [
      'Right people in the right positions',
      'Support all divisions with qualified personnel',
      'Maintain morale and member satisfaction',
      'Develop leadership pipeline'
    ]
  },
  {
    id: 'cso',
    title: 'Chief Security Officer (CSO)',
    icon: <Shield className={styles.roleIcon} />,
    department: 'Security',
    overview: 'The CSO oversees security operations and leads combat strategy planning. Commands all combat and defensive assets during fleet operations.',
    fleetRole: 'Combat Group Commander',
    responsibilities: [
      'Clear hostile threats',
      'Protect all non-combat assets',
      'Manage offensive and defensive actions',
      'Coordinate fighter squadrons and interceptor wings',
      'Establish defensive screens and overwatch elements'
    ],
    commands: [
      'Escorts',
      'Fighter squadrons',
      'Interceptor wings',
      'Defensive screens',
      'Overwatch elements'
    ],
    principles: [
      'Security is paramount for all operations',
      'Proactive threat assessment and neutralization',
      'Coordinate with Intel Group for threat awareness',
      'Balance offense and defense based on mission objectives'
    ]
  },
  {
    id: 'cio',
    title: 'Chief Intelligence Officer (CIO)',
    icon: <Brain className={styles.roleIcon} />,
    department: 'Intelligence',
    overview: 'The CIO is responsible for reconnaissance, strategic information gathering, and analysis. Controls all recon and scanning operations during fleet deployments.',
    fleetRole: 'Intel Group Commander',
    responsibilities: [
      'Report enemy movement',
      'Identify safe/unsafe routes',
      'Provide dynamic recon data',
      'Coordinate scanning and situational awareness',
      'Track threats across operational zones'
    ],
    commands: [
      'Recon ships',
      'Scan teams',
      'Forward observation units',
      'Threat tracking assets'
    ],
    principles: [
      'Information is power - maintain constant awareness',
      'Provide actionable intelligence to Fleet Command',
      'Coordinate with Combat Group on threat data',
      'Scout ahead, report back, stay hidden'
    ]
  },
  {
    id: 'clo',
    title: 'Chief Logistics Officer (CLO)',
    icon: <Package className={styles.roleIcon} />,
    department: 'Logistics',
    overview: 'The CLO heads logistics operations including resource flow and shipment tracking. Ensures all materials, supplies, and cargo vessels move safely and efficiently.',
    fleetRole: 'Logistics Group Commander',
    responsibilities: [
      'Coordinate cargo and supply flow',
      'Control convoy timing',
      'Ensure operational sustainability',
      'Manage haulers and supply craft',
      'Coordinate fuel and utility ships'
    ],
    commands: [
      'Convoys',
      'Cargo haulers',
      'Supply craft',
      'Fuel and utility ships'
    ],
    principles: [
      'No operation succeeds without proper logistics',
      'Coordinate with Combat Group for convoy protection',
      'Maintain supply chain efficiency',
      'Plan for contingencies and backup supplies'
    ]
  },
  {
    id: 'cto',
    title: 'Chief Technology Officer (CTO)',
    icon: <MonitorSmartphone className={styles.roleIcon} />,
    department: 'Technology',
    overview: 'The CTO directs development of organizational systems, tools, and automations. Provides technical advisory support during fleet operations.',
    fleetRole: 'Technical Advisor',
    responsibilities: [
      'Advise fleet command on ship systems',
      'Optimize power and heat management',
      'Improve sensor optimization',
      'Enhance fuel efficiency',
      'Manage radar/scanning systems'
    ],
    commands: [
      'No direct command role',
      'Advisory capacity to all Battle Groups'
    ],
    principles: [
      'Technology enables all operations',
      'Provide technical expertise when needed',
      'Optimize fleet performance through systems knowledge',
      'This is not a command role - support and advise'
    ],
    notes: 'The CTO serves in an advisory capacity, not as a Battle Group Commander.'
  },
  {
    id: 'cmo',
    title: 'Chief Medical Officer (CMO)',
    icon: <Heart className={styles.roleIcon} />,
    department: 'Medical',
    overview: 'The CMO oversees all medical operations for the organization. Commands medical rescue and support operations during fleet deployments.',
    fleetRole: 'Medical Group Commander',
    responsibilities: [
      'Stabilize casualties',
      'Conduct rescue and evacuation',
      'Maintain med-support readiness',
      'Coordinate medical ships and triage wings',
      'Execute emergency medical response'
    ],
    commands: [
      'Medical ships',
      'Evacuation teams',
      'Triage wings',
      'Rescue vessels'
    ],
    principles: [
      'No one gets left behind',
      'Coordinate with Support Group for combined rescue ops',
      'Maintain medical readiness at all times',
      'Triage based on mission criticality and survivability'
    ]
  }
];

const operationalScenarios = [
  {
    id: 'convoy-escort',
    title: 'Large Cargo Convoy with Hostile Threats',
    description: 'A major supply convoy moving valuable cargo through contested space with confirmed hostile activity in the area.',
    roles: [
      {
        position: 'CEO/COO (FLEETCOM)',
        responsibilities: [
          'Set strategic objective: safe delivery of cargo',
          'Determine route and timing',
          'Decide engagement rules (fight or evade)',
          'Authorize withdrawal if needed'
        ]
      },
      {
        position: 'CSO (Combat Group)',
        responsibilities: [
          'Deploy fighter escort and screening ships',
          'Clear hostile threats along route',
          'Coordinate defensive formations',
          'Engage hostiles to protect convoy'
        ]
      },
      {
        position: 'CIO (Intel Group)',
        responsibilities: [
          'Scout ahead for hostile contacts',
          'Identify safe jump points',
          'Monitor enemy fleet movements',
          'Report threat levels in real-time'
        ]
      },
      {
        position: 'CLO (Logistics Group)',
        responsibilities: [
          'Organize convoy formation',
          'Manage hauler speeds and spacing',
          'Coordinate refueling stops',
          'Ensure cargo security'
        ]
      },
      {
        position: 'CMO (Medical Group)',
        responsibilities: [
          'Position medical ships for rapid response',
          'Prepare for potential casualties',
          'Standby for rescue operations',
          'Coordinate with Combat Group for extraction'
        ]
      },
      {
        position: 'CTO (Technical Advisor)',
        responsibilities: [
          'Optimize ship loadouts for convoy speed',
          'Advise on sensor/radar configurations',
          'Recommend countermeasures against threats',
          'Monitor fuel efficiency'
        ]
      }
    ]
  },
  {
    id: 'rescue-operation',
    title: 'Emergency Rescue in Hostile Territory',
    description: 'Organization member down in hostile space requiring immediate extraction under fire.',
    roles: [
      {
        position: 'CEO/COO (FLEETCOM)',
        responsibilities: [
          'Authorize rescue operation',
          'Determine acceptable risk level',
          'Coordinate Battle Group response',
          'Decide if additional support needed'
        ]
      },
      {
        position: 'CMO (Medical Group)',
        responsibilities: [
          'Lead rescue operation',
          'Deploy medical ships to location',
          'Coordinate triage and stabilization',
          'Execute extraction under fire'
        ]
      },
      {
        position: 'CSO (Combat Group)',
        responsibilities: [
          'Clear hostile forces from rescue zone',
          'Provide escort for medical ships',
          'Establish defensive perimeter',
          'Cover extraction'
        ]
      },
      {
        position: 'CIO (Intel Group)',
        responsibilities: [
          'Locate downed member',
          'Identify hostile positions',
          'Find safest approach route',
          'Monitor enemy reinforcements'
        ]
      }
    ]
  },
  {
    id: 'mining-operation',
    title: 'Large-Scale Mining Operation',
    description: 'Multi-ship mining operation in a resource-rich but potentially dangerous sector.',
    roles: [
      {
        position: 'CEO/COO (FLEETCOM)',
        responsibilities: [
          'Approve operation and location',
          'Set profit/safety balance',
          'Authorize force commitment',
          'Monitor overall progress'
        ]
      },
      {
        position: 'CLO (Logistics Group)',
        responsibilities: [
          'Lead mining operation',
          'Coordinate mining ships and haulers',
          'Manage resource collection and transport',
          'Optimize efficiency and yields'
        ]
      },
      {
        position: 'CSO (Combat Group)',
        responsibilities: [
          'Provide security patrol',
          'Respond to pirate threats',
          'Establish safe perimeter',
          'Escort loaded haulers'
        ]
      },
      {
        position: 'CIO (Intel Group)',
        responsibilities: [
          'Scout for high-value resources',
          'Monitor for hostile activity',
          'Identify safe mining zones',
          'Track competing organizations'
        ]
      }
    ]
  },
  {
    id: 'contested-assault',
    title: 'Assault on Contested Facility',
    description: 'Organized attack on an enemy-held facility or station requiring full fleet coordination.',
    roles: [
      {
        position: 'CEO/COO (FLEETCOM)',
        responsibilities: [
          'Define mission objective and success criteria',
          'Authorize engagement and rules of engagement',
          'Coordinate overall fleet maneuvers',
          'Make strategic decisions (press attack or withdraw)'
        ]
      },
      {
        position: 'CSO (Combat Group)',
        responsibilities: [
          'Lead assault operation',
          'Command fighter squadrons and capital ships',
          'Execute tactical combat plans',
          'Coordinate ground and space forces'
        ]
      },
      {
        position: 'CIO (Intel Group)',
        responsibilities: [
          'Provide pre-assault reconnaissance',
          'Identify defensive positions and weaknesses',
          'Monitor enemy reinforcements',
          'Update FLEETCOM on changing battlefield conditions'
        ]
      },
      {
        position: 'CLO (Logistics Group)',
        responsibilities: [
          'Ensure ammunition and supplies available',
          'Coordinate resupply during operation',
          'Position supply ships safely',
          'Prepare for asset recovery post-battle'
        ]
      },
      {
        position: 'CMO (Medical Group)',
        responsibilities: [
          'Stage medical ships at safe distance',
          'Prepare for mass casualties',
          'Execute rapid evacuations',
          'Coordinate with Combat Group for extraction under fire'
        ]
      },
      {
        position: 'CTO (Tech Group)',
        responsibilities: [
          'Advise on target facility systems',
          'Recommend breach tactics',
          'Optimize combat loadouts',
          'Troubleshoot technical issues during assault'
        ]
      }
    ]
  }
];

const LeadershipGuide: React.FC = () => {
  const { user } = useAuth();
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'roles' | 'scenarios'>('roles');
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  // Check if user is Tier 4 (C-Suite)
  const isTier4 = user?.positionTier === 4;

  if (!isTier4) {
    return <Navigate to="/home" />;
  }

  const toggleRole = (roleId: string) => {
    setExpandedRole(expandedRole === roleId ? null : roleId);
  };

  const toggleScenario = (scenarioId: string) => {
    setExpandedScenario(expandedScenario === scenarioId ? null : scenarioId);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Crown className={styles.headerIcon} />
        <h1 className={styles.title}>C-Suite Leadership Guide</h1>
        <p className={styles.subtitle}>
          Fleet Operations Command Structure & Role Responsibilities
        </p>
      </div>

      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'roles' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Role Guides
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'scenarios' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('scenarios')}
        >
          Operational Scenarios
        </button>
      </div>

      {activeTab === 'roles' && (
        <>
          <div className={styles.introduction}>
        <h2>Fleet Command Hierarchy</h2>
        <div className={styles.hierarchyFlow}>
          <div className={styles.hierarchyItem}>Fleet Commander (CEO)</div>
          <div className={styles.hierarchyArrow}>↓</div>
          <div className={styles.hierarchyItem}>Fleet XO (COO)</div>
          <div className={styles.hierarchyArrow}>↓</div>
          <div className={styles.hierarchyItem}>Battle Group Commanders (C-Suite)</div>
          <div className={styles.hierarchyArrow}>↓</div>
          <div className={styles.hierarchyItem}>Task Force Leaders</div>
          <div className={styles.hierarchyArrow}>↓</div>
          <div className={styles.hierarchyItem}>Wing / Section Leaders</div>
          <div className={styles.hierarchyArrow}>↓</div>
          <div className={styles.hierarchyItem}>Operators</div>
        </div>

        <div className={styles.principles}>
          <h3>Core Principles of TFIC Fleet Doctrine</h3>
          <ul>
            <li>Fleet Commander sets strategy; XO ensures execution</li>
            <li>Battle Groups operate independently within their domain</li>
            <li>Task Forces manage specialized mission components</li>
            <li>Wing Leaders handle immediate tactical decisions</li>
            <li>No bypassing the established chain-of-command</li>
            <li>Communication remains clear, concise, and controlled</li>
            <li>Each division supports the fleet without overlapping authority</li>
          </ul>
        </div>
      </div>

      <div className={styles.rolesSection}>
        <h2>Role-Specific Guides</h2>
        <p className={styles.rolesIntro}>
          Click on any role below to view detailed responsibilities, command structure, and operational principles.
        </p>

        {roleGuides.map((role) => (
          <div key={role.id} className={styles.roleCard}>
            <div
              className={styles.roleHeader}
              onClick={() => toggleRole(role.id)}
            >
              <div className={styles.roleHeaderLeft}>
                {role.icon}
                <div className={styles.roleHeaderText}>
                  <h3>{role.title}</h3>
                  <span className={styles.roleDepartment}>{role.department}</span>
                </div>
              </div>
              <div className={styles.roleHeaderRight}>
                {expandedRole === role.id ? (
                  <ChevronUp className={styles.chevron} />
                ) : (
                  <ChevronDown className={styles.chevron} />
                )}
              </div>
            </div>

            {expandedRole === role.id && (
              <div className={styles.roleContent}>
                <div className={styles.section}>
                  <h4>Overview</h4>
                  <p>{role.overview}</p>
                </div>

                <div className={styles.section}>
                  <h4>Fleet Operations Role</h4>
                  <div className={styles.fleetRole}>{role.fleetRole}</div>
                </div>

                <div className={styles.section}>
                  <h4>Key Responsibilities</h4>
                  <ul>
                    {role.responsibilities.map((resp, idx) => (
                      <li key={idx}>{resp}</li>
                    ))}
                  </ul>
                </div>

                <div className={styles.section}>
                  <h4>Command Authority</h4>
                  <ul>
                    {role.commands.map((cmd, idx) => (
                      <li key={idx}>{cmd}</li>
                    ))}
                  </ul>
                </div>

                <div className={styles.section}>
                  <h4>Operational Principles</h4>
                  <ul>
                    {role.principles.map((principle, idx) => (
                      <li key={idx}>{principle}</li>
                    ))}
                  </ul>
                </div>

                {role.notes && (
                  <div className={styles.notesSection}>
                    <strong>Important Note:</strong> {role.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
        </>
      )}

      {activeTab === 'scenarios' && (
        <div className={styles.scenariosSection}>
          <div className={styles.scenariosIntro}>
            <h2>Operational Scenarios</h2>
            <p>
              Real-world examples of fleet operations showing how each C-Suite role contributes
              to mission success. These scenarios demonstrate coordination, responsibilities, and
              decision-making across the command structure.
            </p>
          </div>

          {operationalScenarios.map((scenario) => (
            <div key={scenario.id} className={styles.scenarioCard}>
              <div
                className={styles.scenarioHeader}
                onClick={() => toggleScenario(scenario.id)}
              >
                <div className={styles.scenarioHeaderLeft}>
                  <h3>{scenario.title}</h3>
                  <p className={styles.scenarioDescription}>{scenario.description}</p>
                </div>
                <div className={styles.scenarioHeaderRight}>
                  {expandedScenario === scenario.id ? (
                    <ChevronUp className={styles.chevron} />
                  ) : (
                    <ChevronDown className={styles.chevron} />
                  )}
                </div>
              </div>

              {expandedScenario === scenario.id && (
                <div className={styles.scenarioContent}>
                  <h4>Role Responsibilities</h4>
                  {scenario.roles.map((role, idx) => (
                    <div key={idx} className={styles.scenarioRole}>
                      <div className={styles.scenarioRoleTitle}>{role.position}</div>
                      <ul className={styles.scenarioRoleList}>
                        {role.responsibilities.map((resp, respIdx) => (
                          <li key={respIdx}>{resp}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <p>
          This guide is based on the TFIC Fleet Operations Command Guide and ensures
          predictable leadership, clear command authority, and efficient coordination
          during all organizational operations.
        </p>
      </div>
    </div>
  );
};

export default LeadershipGuide;
